import React from "react";
import GenericLayout from "layouts/generic";
import css from "./index.module.scss";
import FloatingImageSection from "sections/floatingImage";
import Card from "@/components/card";
import StripedCtaSection from "sections/stripedCta";
import TacticsSection from "sections/tactics";
import Button from "@/components/button";
import FloatingImage from "@/components/floatingImage";
import TestimonialsSection from "sections/testimonials";
import ContactSection from "sections/contact";
import CaseStudySection from "/sections/caseStudySection";
import Picture from "@/components/picture";
import BobbingArrow from "@/components/bobbingArrow";

export default function GrowthMarketingAgencyPage() {
  return <GenericLayout
    title="Stunt Marketing | Deviate Labs"
    description="If your startup is seeking to grab attention with an effective marketing stunt, Deviate Labs can help you attract eyeballs - and customers."
    color="purple"
  >
    <FloatingImageSection
      headlines={["Stunt Marketing", "for Growth at", "Deviate Labs"]}
      subheadlines = {["If your startup is seeking to grab attention with an effective marketing stunt, Deviate Labs can help you attract eyeballs — and customers."]}
      floatingImageBase="/images/floatingImages/breakTheCodeVertical/breakthecode"
      floatingImageSizes={["180", "384", "640"]}
      floatingImageLoading="eager"
      ctaLabel="Let's Talk"
      ctaUrl="#contact"
      reversed
    >
      <p>Deviate Labs is a growth marketing agency established in 2013. We offer a wide spectrum of custom-tailored marketing services, including stunt marketing, and we can work in an advisory or execution role. Here are a couple examples of marketing stunts we&apos;ve launched:</p>
      <ul>
        <li>A series of digital puzzle rooms dubbed Break the Code designed to break through the Black Friday noise and engage a notoriously hard-to-reach demographic: developers. The stunt spawned a myriad of media, ranging from YouTube tutorials to a dedicated Discord server, to a 5,000+ comment Reddit megathread. In the end, over 193,000 techies tried their hand at Break the Code.</li>
        <li>The Immersive Audio Album Awards were created to capture an abandoned audience of surround sound and immersive music fans in 2020 when the GRAMMY&apos;s temporarily paused their &quot;Best Immersive Audio Album Award.&quot; Their reasoning, that the category required groups of in-person listening during Covid, didn&apos;t resonate with audiophiles who were eager to celebrate their favorite artists. ImmersiveAudioAlbum.com surpassed daily and monthly user records and nearly doubled the leads in its email newsletter list when they hosted a digital, fan-based award in the GRAMMY&apos;s stead.</li>
      </ul>
      <p>Our stunt marketing campaigns have been featured on media outlets like CBS, VentureBeat, and NPR and have generated long lasting brand awareness and annualized events that have led to continued growth for our clients.</p>
    </FloatingImageSection>

    <section className={`section ${css.what}`}>
      <div className="sectionInner">
        <h2 className="underlined">What is Stunt Marketing?</h2>
        <p>The technical definition of stunt marketing is: an event or spectacle engineered to garner earned media and word-of-mouth virality.</p>
        <p>In practice, stunt marketing is an art form that combines creative messaging, thoughtful timing, and daring execution to capture maximum media and public attention. Stunt marketing requires a big statement and bold tactics. It&apos;s about creating something remarkable that your audience won&apos;t soon forget and doing so with a purpose. Effective stunt marketing won&apos;t simply grab attention; it will also direct it to something specific, such as your organization, product, or cause.</p>
        <p>Deviate Labs&apos; growth marketing ideology is rooted in marketing fundamentals steeped with battle-tested strategies. And with the right circumstances and clients, we have had the opportunity to implement experimental viral stunt marketing acts. </p>
      </div>
    </section>

    <section className={`section ${css.why}`}>
      <div className="sectionInner">
        <h2 className="underlined">Why Companies Choose Stunt Marketing</h2>
        <p>Stunt marketing appeals to many organizations because of its potential to go viral and reach a wide audience — sometimes for very minimal spend. This quick increase in visibility can have an explosive impact on an organization&apos;s sales.</p>

        <h3>Famous Stunt Marketing Campaigns</h3>
        <p>Here are some famous examples of successful stunt marketing you may be familiar with:</p>

        <figure className={css.right}>
          <Picture
            base="/images/misc/iceBucket/iceBucketChallenge"
            alt="a group of people dumping ice water on themselves"
            exts={["webp", "png"]}
            widths={[1000, 750, 500]}
            loading="lazy"
            sizes="(min-width:900px) 500px, 100vw"
          />
          <figcaption>The viral Ice Bucket Challenge.</figcaption>
        </figure>

        <p><b>The Ice Bucket Challenge:</b> In 2014, the ALS Ice Bucket Challenge went mega-viral. Thousands of people including celebrities and politicians posted videos of themselves getting an entire bucket of icy water dumped on them to increase awareness and raise money for the ALS Association. The Ice Bucket Challenge was one of the most successful stunt marketing campaigns in recent memory.</p>

        <p><b>The Old Spice Guy:</b> Old Spice&apos;s YouTube views and sales soared as a result of their <a className="link" href="https://youtu.be/eiO_JES4yBY">&quot;Old Spice Guy&quot; campaign</a>, featuring actor Isaiah Mustafa. The quirky, funny, one-shot commercials were different and memorable, so they garnered attention and, as a result, new customers.</p>

        <p><b>Tesla&apos;s First Car in Space:</b> Tesla is no stranger to marketing stunts to generate PR and sales. In 2018, Tesla launched its new Roadster models into space via a SpaceX rocket ship, inspiring awe as an &apos;out of world&apos; luxury automobile.</p>

        <h3>Famous Failed Stunts</h3>

        <p>Although stunt marketing can be viral and wildly successful, poorly executed stunt marketing can go very wrong and attract attention in unintended ways. It&apos;s important to choose an experienced growth marketing agency to craft your marketing stunt so that your company doesn&apos;t sacrifice brand damage and customer attrition for the chance at virality and explosive growth. The following are examples of failed stunts that grabbed attention in unintended ways:</p>

        <figure className={css.left}>
          <Picture
            base="/images/misc/moldyWhopper/moldyWhopper"
            alt="a moldy burger"
            exts={["webp", "png"]}
            widths={[1000, 750, 500]}
            loading="lazy"
            sizes="(min-width:900px) 500px, 100vw"
          />
          <figcaption>A lightning rod for negative sentiment.</figcaption>
        </figure>

        <p><b>The Pepsi Challenge:</b> Starting in 1975, the iconic Pepsi Challenge asked soda drinkers to choose between Pepsi and Coke in a blind taste test. While the challenge itself was hugely popular, and more people than expected preferred Pepsi based on a single sip, Pepsi dropped the ball when it came to actually changing soda drinkers&apos; buying habits. Instead of giving the Pepsi-choosers coupons or other incentives to continue purchasing Pepsi, shoppers quickly reverted to buying Coke. All the new eyes on Pepsi were effectively squandered.
        </p>
        <p><b>Burger King&apos;s Moldy Whopper:</b> In an attempt to highlight the freshness of their ingredients (and a lack of preservatives), Burger King featured photos of a moldy Whopper in a 2020 ad campaign. While the shocking photos were a lightning rod for attention and strong emotion, the attention was negative and the emotion was — primarily — disgust.</p>
        <p>The tradeoff is this: companies risk brand damage and customer attrition for the chance at virality and explosive growth. Luckily, working with an experienced growth marketing agency can help mitigate the risks associated with an attention-grabbing stunt.</p>
      </div>
    </section>

    <section className={`section ${css.threeExamples}`}>
      <div className="sectionInner">
        <h2>Three Stunt Marketing Case Studies</h2>
        <p>Deviate Labs&apos; growth marketing ideology is rooted in marketing fundamentals steeped with battle-tested strategies. However, with the right circumstances and clients, we have had the opportunity to implement experimental stunts. Following are three examples whose results exceeded even our own expectations, with some confidential details omitted for client privacy.</p>
        <BobbingArrow href="#xsite"/>

      </div>
    </section>

    <CaseStudySection
      headline='Using "X" to Mark the Stunt'
      floatingImage={{
        file: "/images/floatingImages/xsite/xsite",
        sizes: ["180", "384", "640"],
        loading: "lazy",
        caption: "Our X puzzle game attracted 100k new fans.",
      }}
      reversed
      className={css.caseStudy}
      id="xsite"
    >
      <p>To capture interest for a musician&apos;s 10th album release — symbolically and cryptically titled X — we created an infinite-loop <a className="link" href="https://xmarksthe.site/">digital art piece</a>. We also threw in a series of custom audio-based puzzles (tied to specific objects in the art) and prizes for winners (including a binaural headset, an infinite art smartframe, and gift cards to Apple, Amazon, Tidal, and more).</p>
      <p>To create the art, we chose to engage Peruvian artist Knot Paolo who has created moving art (used digitally and for immersive retail experiences) for Fendi, H&M, Ralph Lauren, China Duty Free stores, and more. Paolo&apos;s whimsy-surrealist style punctuated the nostalgic yet forward-facing walk down memory lane through the artist&apos;s 10 albums.</p>
      <p>By engaging specific groups — primarily puzzle solvers and audiophiles who owned the right equipment to solve the puzzles — we created a microcosm community and with it, virality. The stunt eventually attracted 100,000 new fans for our client&apos;s music.</p>
    </CaseStudySection>

    <CaseStudySection
      headline="Burying Treasure to Excavate Views"
      floatingImage={{
        file: "/images/floatingImages/sparrow/sparrow",
        sizes: ["180", "384", "640"],
        loading: "lazy",
        caption: "This Jack Sparrow lookalike attracted 10k+ views.",
      }}
      className={css.caseStudy}
      id="yoyoboat"
    >
      <p>A boat-sharing company modeled after AirBnB for water lovers retained Deviate Labs to facilitate expansion from the seasonal midwest to the favorable year-round boating conditions of Florida Intracoastal Waterway.</p>
      <p>While the weather of the Gulf was attractive, the boating market in the region was nearly impenetrable due to well-established mom and pop businesses in the area.</p>
      <p>To create a playful buzz in this anchored market, we created a pirate-themed family friendly treasure hunt complete with an actual buried treasure of every pirate&apos;s favorite precious metal: gold.</p>
      <p>An especially viral component of the campaign was the Jack Sparrow lookalike we engaged to create a series of videos to promote and implement the treasure hunt. The Sparrow stunt attracted tens of thousands of views and significant brand affinity in a challenging market.</p>
    </CaseStudySection>

    <CaseStudySection
      headline="From Creep Out to Opt In"
      floatingImage={{
        file: "/images/floatingImages/cadenTerminal/cadenTerminal",
        sizes: ["180", "384", "640"],
        loading: "lazy",
        caption: "The Caden Terminal.",
      }}
      reversed
      className={css.caseStudy}
      id="caden"
    >
      <p>Charged with acquiring 10,000 beta signups for an app in prelaunch, we designed a marketing stunt that would show each lead&apos;s personal data that would both entice and alarm Internet users.</p>
      <p>Our client&apos;s app would eventually allow users to store and monetize their personal data on their terms — as opposed to the current system, which allows anonymous data brokers to collect and sell users data without sharing the profits (or even the knowledge that their data is being used this way).</p>
      <p>In order to collect signups, we needed to simultaneously educate our audience about the creep factor associated with current data practices and create comfort around sharing their email address with Caden.</p>
      <p>We were able to achieve the 10,000 signup goal with only $350 of spend by creating an 80s inspired data terminal where users could enter their email to reveal the personal information about them that was currently being bought and sold online. A niche, brand-aligned influencer was the spark that set the virality fire ablaze.</p>
    </CaseStudySection>

    <StripedCtaSection headline="Ready for Growth?" ctaHref="#contact"/>

    <TestimonialsSection nTestimonials="12" aboutLink className={css.testimonials}/>

    <ContactSection/>
  </GenericLayout>;
}

